import React, { useEffect, useState } from 'react';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, MenuItem, Alert, Stack } from '@mui/material';
import { DataGrid, GridColDef, GridToolbar } from '@mui/x-data-grid';
import AddIcon from '@mui/icons-material/Add';
import PageHeader from '../components/PageHeader';
import CustomNoRowsOverlay from '../components/CustomNoRowsOverlay';
import api from '../api/api';
import { useAuth } from '../context/AuthContext';

interface Course {
  id: number;
  title: string;
}
interface Camera {
  id: number;
  name: string;
}
interface ClassSession {
  id: number;
  course_id: number;
  camera_id: number;
  start_time: string;
  end_time: string;
  course: Course;
  camera: Camera;
}

const ClassSessions: React.FC = () => {
  const { user } = useAuth();
  const [sessions, setSessions] = useState<ClassSession[]>([]);
  const [courses, setCourses] = useState<Course[]>([]);
  const [cameras, setCameras] = useState<Camera[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [newSession, setNewSession] = useState({ course_id: '', camera_id: '', start_time: '', end_time: '' });
  const [actionError, setActionError] = useState('');

  const fetchData = async () => {
    setLoading(true);
    setError('');
    try {
      // Fetch courses and cameras for the admin's school
              const [coursesRes, camerasRes] = await Promise.all([
          api.get('/courses/', { params: { school_id: user?.school_id } }),
        api.get('/cameras/', { params: { school_id: user?.school_id } }),
      ]);
      setCourses(coursesRes.data as Course[]);
      setCameras(camerasRes.data as Camera[]);
      // Fetch all sessions for all courses
      const sessionsArr: ClassSession[] = [];
      for (const course of coursesRes.data as Course[]) {
        const res = await api.get(`/courses/sessions/${course.id}`);
        sessionsArr.push(...(res.data as ClassSession[]));
      }
      setSessions(sessionsArr);
    } catch (err: any) {
      setError('Failed to load sessions');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.role === 'admin' || user?.role === 'superuser') {
      fetchData();
    }
  }, [user]);

  const handleAdd = async () => {
    setActionError('');
    try {
      await api.post('/courses/sessions', {
        course_id: Number(newSession.course_id),
        camera_id: Number(newSession.camera_id),
        start_time: newSession.start_time,
        end_time: newSession.end_time,
      });
      setAddDialogOpen(false);
      setNewSession({ course_id: '', camera_id: '', start_time: '', end_time: '' });
      fetchData();
    } catch (err: any) {
      setActionError(err?.response?.data?.detail || 'Failed to add session');
    }
  };

  const columns: GridColDef[] = [
    { field: 'id', headerName: 'ID', width: 90 },
    {
      field: 'course',
      headerName: 'Course',
      flex: 1,
      minWidth: 150,
      valueGetter: (params: any) => {
        try {
          return params.row?.course?.title || '';
        } catch {
          return '';
        }
      },
    },
    {
      field: 'camera',
      headerName: 'Camera',
      flex: 1,
      minWidth: 150,
      valueGetter: (params: any) => {
        try {
          return params.row?.camera?.name || '';
        } catch {
          return '';
        }
      },
    },
    { field: 'start_time', headerName: 'Start Time', flex: 1, minWidth: 180 },
    { field: 'end_time', headerName: 'End Time', flex: 1, minWidth: 180 },
  ];

  if (user?.role !== 'admin' && user?.role !== 'superuser') {
    return <Alert severity="error">You are not authorized to view this page.</Alert>;
  }

  return (
    <Box>
      <PageHeader title="Class Sessions">
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setAddDialogOpen(true)}>
          Add Session
        </Button>
      </PageHeader>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <Box sx={{ height: 500, width: '100%' }}>
        <DataGrid
          rows={sessions}
          columns={columns}
          loading={loading}
          pageSizeOptions={[10, 25, 50]}
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
          slots={{ toolbar: GridToolbar, noRowsOverlay: () => <CustomNoRowsOverlay message="No Sessions Found" /> }}
          disableRowSelectionOnClick
          getRowId={row => row.id}
        />
      </Box>
      {/* Add Dialog */}
      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)}>
        <DialogTitle>Add Class Session</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              select
              label="Course"
              value={newSession.course_id}
              onChange={e => setNewSession(s => ({ ...s, course_id: e.target.value }))}
              fullWidth
            >
              {courses.map(course => (
                <MenuItem key={course.id} value={course.id}>{course.title}</MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Camera"
              value={newSession.camera_id}
              onChange={e => setNewSession(s => ({ ...s, camera_id: e.target.value }))}
              fullWidth
            >
              {cameras.map(camera => (
                <MenuItem key={camera.id} value={camera.id}>{camera.name}</MenuItem>
              ))}
            </TextField>
            <TextField
              label="Start Time"
              type="datetime-local"
              value={newSession.start_time}
              onChange={e => setNewSession(s => ({ ...s, start_time: e.target.value }))}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="End Time"
              type="datetime-local"
              value={newSession.end_time}
              onChange={e => setNewSession(s => ({ ...s, end_time: e.target.value }))}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
            {actionError && <Alert severity="error">{actionError}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleAdd} variant="contained">Add</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ClassSessions; 